
import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { PageSEO } from '@/components/seo/PageSEO';

// Import components
import { SignInForm } from '@/components/auth/SignInForm';
import { ResetPasswordDialog } from '@/components/auth/ResetPasswordDialog';

// Import shadcn UI components
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [authDebug, setAuthDebug] = useState({
    attempts: 0,
    lastAttemptTime: null as number | null,
    authState: { isAuthenticated, loading, user: user?.email || null }
  });

  // Get the return URL from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  // Debug logging - only update when auth state actually changes
  // to prevent infinite loop
  useEffect(() => {
    const authState = { isAuthenticated, loading, user: user?.email || null };
    const stateChanged = 
      authState.isAuthenticated !== authDebug.authState.isAuthenticated ||
      authState.loading !== authDebug.authState.loading ||
      authState.user !== authDebug.authState.user;
    
    if (stateChanged) {
      setAuthDebug(prev => ({
        attempts: prev.attempts + 1,
        lastAttemptTime: Date.now(),
        authState
      }));
      
      console.log(`Auth state in SignIn (update ${authDebug.attempts + 1}):`, {
        isAuthenticated,
        loading,
        user: user?.email || null,
        timestamp: new Date().toISOString(),
        returnTo: from
      });
    }
  }, [isAuthenticated, loading, user, from, authDebug.authState]);

  // Handle redirect in useEffect to avoid React state updates during render
  useEffect(() => {
    if (!loading && isAuthenticated) {
      console.log("SignIn: User is authenticated, redirecting to:", from);
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, from]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-md w-full p-6 text-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <h3 className="text-lg font-medium mb-2">Checking authentication status...</h3>
          <p className="text-sm text-muted-foreground">This should only take a moment.</p>
          
          {/* Add a manual refresh button that appears after a few seconds */}
          <div className="mt-8">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Don't render the sign-in form if user is authenticated
  // The redirection will happen via the useEffect hook
  if (isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-md w-full p-6 text-center">
          <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <h3 className="text-lg font-medium mb-2">You're authenticated!</h3>
          <p className="text-sm text-muted-foreground">Redirecting to {from === '/dashboard' ? 'dashboard' : 'previous page'}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-background">
      <PageSEO 
        title="Sign In - Retail Ranker"
        description="Sign in to your Retail Ranker account and access your local business dashboard."
        keywords="login, sign in, local business, retail ranker, business dashboard"
        canonicalUrl="/sign-in"
        structuredData={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Sign In - Retail Ranker",
          "description": "Sign in to your Retail Ranker account to access your local business dashboard."
        }]}
      />
      
      <div className="w-full max-w-md">
        <Button 
          variant="ghost" 
          size="sm" 
          className="mb-2" 
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        
        {authDebug.attempts > 3 && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Authentication Debug Info</AlertTitle>
            <AlertDescription className="text-xs">
              Auth State: {JSON.stringify(authDebug.authState, null, 2)}
              <br />
              Attempts: {authDebug.attempts}
              <br />
              Return Path: {from}
            </AlertDescription>
          </Alert>
        )}
        
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Sign in to your account</CardTitle>
            <CardDescription>
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SignInForm onForgotPassword={() => setOpenResetDialog(true)} />
          </CardContent>
          <CardFooter className="flex justify-center">
            <div className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/sign-up" className="text-primary underline hover:text-primary/90">
                Sign Up
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
      
      {/* Password Reset Dialog */}
      <ResetPasswordDialog 
        open={openResetDialog}
        onOpenChange={setOpenResetDialog}
      />
    </div>
  );
};

export default SignIn;
